import React, {useEffect, useState, Fragment} from "react";
import {Controller, UseFormReturn} from "react-hook-form";
import {IAddressResponseData, ICountriesLookup, IStateLookup} from "@connox/typescript-library";
import {useTranslation} from "react-i18next";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {getShopId} from "../../../lib/getShopId";
import Modal from "../../../lib/ModalWindow/Modal";
import {isoLocales} from "../../../lib/payments/PaymentController";
import {isSameAddress} from "./AddressStep";
import {emailRegExp, mailCheckRun} from "../../../../helpers/emailRegExp";
import styles from "./AddressForm.module.scss";
import selectStyles from "../../../newsletter/NewsLetterOptionsForm.module.scss";
import Select from "../../../lib/form/Select/Select";
import {useNonInitialEffect} from "../../../../helpers/useNonInitialEffect";
import {useCustomerSelector} from "../../../../store";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import {StatesHelper} from "./StatesHelper";

type DeliveryTypesType = "private" | "external";

export type IAddressFormData = IAddressResponseData & {
  vatNumber: string | null;
};

const AddressForm: React.FC<{
  onChange: (data: IAddressFormData) => void;
  methods: UseFormReturn<IAddressResponseData & {vatNumber: string | null}>;
  countries: ICountriesLookup[];
  isDelivery?: boolean;
  phoneError: string; // unexpected phone error comes from server with 400 responce
  lastAddress: IAddressFormData;
  // eslint-disable-next-line complexity
}> = ({onChange, methods, isDelivery = false, phoneError, countries, lastAddress}) => {
  const {t} = useTranslation(["errorcodes", "translations"]);
  const {customer} = useCustomerSelector();
  const {
    register,
    watch,
    formState: {errors},
    trigger,
    setValue,
    reset,
    control,
  }: UseFormReturn<IAddressFormData> = methods;
  const {shippingOptions} = useCheckoutViewContext();
  const packingstationShippingExists = shippingOptions.some((option) => option.isPackingStationAllowed);
  const postofficeShippingExists = shippingOptions.some((option) => option.isPostOfficeAllowed);
  const {cartData, deliveryAddress, invoiceAddress} = useCheckoutContext().checkout;
  const {isCorporateCustomer, vatNumber, countryCode} = cartData;
  const [states, setStates] = useState<IStateLookup[]>([]);
  const [isZipValid, setIsZipValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);

  // phone error comes with address data
  const [addressPhoneError, setAddressPhoneError] = useState(phoneError);
  const defaultInvoiceCountryCode =
    isSameAddress(deliveryAddress, invoiceAddress) || !deliveryAddress
      ? countryCode
      : invoiceAddress?.countryCode ?? countryCode;

  const gender = (isDelivery ? deliveryAddress?.gender : invoiceAddress?.gender) || lastAddress.gender;
  const email = invoiceAddress?.email || lastAddress.email || customer.email || "";

  const defaultAddress: IAddressFormData = {
    ...((isDelivery ? deliveryAddress : invoiceAddress) || lastAddress),
    vatNumber,
    countryCode: isDelivery ? countryCode : defaultInvoiceCountryCode,
    email,
  };

  const [deliveryType, setDeliveryType] = useState<DeliveryTypesType>(() => {
    if (!isDelivery || getShopId() !== 100001 || (!packingstationShippingExists && !postofficeShippingExists)) {
      return "private";
    }

    return ["Packstation", "Postfiliale"].includes(defaultAddress.street) ? "external" : "private";
  });

  const MAX_LENGTH = 50;

  useEffect(() => {
    if (!invoiceAddress) {
      reset(defaultAddress);
    }
  }, [lastAddress]);

  useNonInitialEffect(() => {
    if (!isDelivery) {
      return;
    }
    const isStreetExternal = ["Packstation", "Postfiliale"].includes(defaultAddress.street);
    if (deliveryType === "private") {
      setValue("street", isStreetExternal ? "" : defaultAddress.street);
      setValue("number", isStreetExternal ? "" : defaultAddress.number);
    } else {
      setValue(
        "street",
        isStreetExternal ? defaultAddress.street : packingstationShippingExists ? "Packstation" : "Postfiliale",
      );
      setValue("number", isStreetExternal ? defaultAddress.number : "");
    }
    reset(watch());
    onChange(watch());
  }, [deliveryType]);

  // this effect will update the error state for zip and email after every call
  useEffect(() => {
    setIsZipValid(!defaultAddress.messages?.some((msg) => msg.field === "zip"));
    setIsEmailValid(!defaultAddress.messages?.some((msg) => msg.field === "email"));
  }, [defaultAddress.messages]);

  useEffect(() => {
    setIsZipValid(true);
  }, [countryCode]);

  // this effect will keep tracking the phone validation.
  useEffect(() => {
    if (watch().phone) {
      if (!phoneError) {
        setAddressPhoneError(
          defaultAddress.messages?.find((msg) => msg.type === "error" && msg.code.includes("PHONE"))?.code || "",
        );
      } else setAddressPhoneError(phoneError);
    } else {
      setAddressPhoneError("");
    }
  }, [defaultAddress.messages, phoneError]);

  // this effect will initialize country value and states in case the counrty in the default address in not valid for shipping
  useEffect(() => {
    if (!countries.some((country) => country.countryCode === defaultAddress.countryCode)) {
      setValue("countryCode", DefaultCountryCode[getShopId()]);
      setStates([]);
    }
  }, [countries]);

  useEffect(() => {
    if (!packingstationShippingExists && !postofficeShippingExists) {
      setDeliveryType("private");
    }
  }, [packingstationShippingExists, postofficeShippingExists]);

  const updateStates = async () => {
    const selectedCountry = countries.find((country) => country.countryCode === watch().countryCode);
    if (selectedCountry && selectedCountry.isStateRequired) {
      const statesData = await StatesHelper.getStates(getShopId(), watch().countryCode);
      setStates(statesData);
      setValue("state", defaultAddress.state);
      await trigger("state");
    } else {
      setValue("state", "");
      setStates([]);
    }
    onChange(watch());
  };

  // this effect will get states after every change to the country
  useEffect(() => {
    void updateStates();
  }, [watch().countryCode]);

  const MobileIcon: React.FC = () => {
    const [open, setOpen] = useState(false);

    return (
      <Fragment>
        <span
          className={`icon icon-info ${styles.infoIcon}`}
          onClick={() => {
            setOpen(!open);
          }}
        />
        <Modal isVisible={open} closeFunction={() => setOpen(false)}>
          <p>{t("general.yourPhoneNumberToFacilitateContact")}</p>
        </Modal>
      </Fragment>
    );
  };

  const zipMaxLength = (): number => {
    switch (watch().countryCode) {
      case "AT":
      case "CH":
        return 4;
      case "DE":
        return 5;
      default:
        return 20;
    }
  };

  const updateForm = () => {
    onChange(watch());
  };

  const isExternalAddress = deliveryType === "external";

  const DeliveryTypes: React.FC = () => {
    return (
      <div className={styles.deliveryTypesWrapper}>
        <div
          className={`${styles.deliveryTypesItem} ${!isExternalAddress ? styles.selected : ""}`}
          onClick={() => {
            setDeliveryType("private");
          }}
        >
          <span className="icon icon-house icon-size--32" />
          {t("form.desiredAddress")}
        </div>
        <div
          className={`${styles.deliveryTypesItem} ${isExternalAddress ? styles.selected : ""}`}
          onClick={() => {
            setDeliveryType("external");
          }}
        >
          <span className="icon-pack-station">&#160;</span>
          {t("checkout.dhlPackingStation")}
        </div>
      </div>
    );
  };

  return (
    <div>
      {(packingstationShippingExists || postofficeShippingExists) && isDelivery && getShopId() === 100001 && (
        <DeliveryTypes />
      )}
      <form className={styles.addressForm} onChange={updateForm}>
        {/* Keep in mind, there's another company input for non-corporate customers */}
        {isCorporateCustomer && (
          <div className={styles.formRow}>
            <div className={styles.inputWrapper}>
              <input
                placeholder={t("form.company")}
                {...register("company", {
                  maxLength: {
                    value: MAX_LENGTH,
                    message: t("form.maxOfCharacters", {count: MAX_LENGTH}),
                  },
                })}
              />
              {errors.company && <span>{errors.company.message}</span>}
            </div>
          </div>
        )}

        {isCorporateCustomer && (
          <div className={styles.formRow}>
            <div className={styles.inputWrapper}>
              <input
                placeholder={t("form.department")}
                {...register("department", {
                  maxLength: {
                    value: MAX_LENGTH,
                    message: t("form.maxOfCharacters", {count: MAX_LENGTH}),
                  },
                })}
              />
              {errors.department && <span className={styles.error}>{errors.department.message}</span>}
            </div>
          </div>
        )}

        {!gender && (
          <div className={styles.formRow}>
            <div className={`${styles.inputWrapper} ${styles.small}`}>
              <Controller
                control={control}
                render={({field}) => (
                  <Select
                    styles={{wrapper: selectStyles.select}}
                    value={watch().gender ?? undefined}
                    onChange={(value) => {
                      field.onChange(value);
                      updateForm();
                    }}
                    placeHolder={t("form.title") + " **"}
                    options={[
                      {value: "F", text: t("form.ms")},
                      {value: "M", text: t("form.mr")},
                    ]}
                  />
                )}
                name={"gender"}
                rules={{required: t("form.chooseGender")}}
              />
              {errors.gender && (
                <span className={styles.error} style={{whiteSpace: "nowrap"}}>
                  {errors.gender.message}
                </span>
              )}
            </div>
          </div>
        )}

        <div className={styles.formRow}>
          <div className={`${styles.inputWrapper} ${styles.medium}`}>
            <input
              placeholder={t(isCorporateCustomer ? "form.attnFirstname" : "form.firstname") + " **"}
              {...register("firstName", {
                required: t("form.pleaseComplete"),
                maxLength: {
                  value: MAX_LENGTH,
                  message: t("form.maxOfCharacters", {count: MAX_LENGTH}),
                },
              })}
            />
            {errors.firstName && <span className={styles.error}>{errors.firstName.message}</span>}
          </div>

          <div className={`${styles.inputWrapper} ${styles.medium}`}>
            <input
              placeholder={t(isCorporateCustomer ? "form.attnLastname" : "form.lastname") + " **"}
              {...register("lastName", {
                required: t("form.pleaseComplete"),
                maxLength: {
                  value: MAX_LENGTH,
                  message: t("form.maxOfCharacters", {count: MAX_LENGTH}),
                },
              })}
            />
            {errors.lastName && <span className={styles.error}>{errors.lastName.message}</span>}
          </div>
        </div>

        {/* Keep in mind, there's a company input for actual corporate customers */}
        {!isCorporateCustomer && (
          <div className={styles.formRow}>
            <div className={styles.inputWrapper}>
              <input
                placeholder={t("form.companyAndAddressAddition")}
                {...register("company", {
                  maxLength: {
                    value: MAX_LENGTH,
                    message: t("form.maxOfCharacters", {count: MAX_LENGTH}),
                  },
                })}
              />
              {errors.company && <span>{errors.company.message}</span>}
            </div>
          </div>
        )}

        {isExternalAddress && isDelivery && (
          <div className={styles.formRow}>
            <div className={`${styles.inputWrapper} ${styles.medium}`}>
              <label className={`${styles.inputRadio} ${!packingstationShippingExists ? styles.disabled : ""}`}>
                <input
                  type="radio"
                  id="Packstation"
                  value="Packstation"
                  checked={watch().street === "Packstation"}
                  disabled={!packingstationShippingExists}
                  {...register("street", {required: true})}
                />
                Packstation
              </label>
            </div>

            <div className={`${styles.inputWrapper} ${styles.medium}`}>
              <label className={`${styles.inputRadio} ${!postofficeShippingExists ? styles.disabled : ""}`}>
                <input
                  type="radio"
                  id="Postfiliale"
                  value="Postfiliale"
                  checked={watch().street === "Postfiliale"}
                  disabled={!postofficeShippingExists}
                  {...register("street", {required: true})}
                />
                Postfiliale
              </label>
            </div>
          </div>
        )}

        <div
          className={`${styles.formRow} ${
            ["GB", "FR", "US"].includes(DefaultCountryCode[getShopId()]) ? styles.reverse : ""
          }`}
        >
          {!isExternalAddress ? (
            <Fragment>
              {/* Default Address with street and house number */}

              <div className={`${styles.inputWrapper} ${styles.big}`}>
                <input
                  placeholder={t("form.street") + " **"}
                  {...register("street", {required: t("form.pleaseComplete")})}
                />
                {errors.street && <span className={styles.error}>{errors.street.message}</span>}
              </div>

              <div className={`${styles.inputWrapper} ${styles.small}`}>
                <input
                  placeholder={t("form.number") + " **"}
                  {...register("number", {
                    required: t("form.pleaseComplete"),
                    maxLength: {value: 5, message: t("form.maxOfCharactersAnotherField")},
                  })}
                />
                {errors.number && <span className={styles.error}>{errors.number.message}</span>}
              </div>
            </Fragment>
          ) : (
            <Fragment>
              {/* Address for post office or packing station with post number and p/o or p/s number */}
              <div className={`${styles.inputWrapper} ${styles.medium}`}>
                <input
                  placeholder={"Postnummer **"}
                  {...register("addressSupplement", {
                    required: t("form.pleaseComplete"),
                    pattern: {value: /^[0-9]{6,10}$/, message: "Bitte eine gültige Postnummer eingeben"},
                  })}
                />
                {errors.addressSupplement && <span className={styles.error}>{errors.addressSupplement.message}</span>}
              </div>

              <div className={`${styles.inputWrapper} ${styles.medium}`}>
                <input
                  placeholder={`Nummer der ${watch().street} **`}
                  {...register("number", {
                    required: t("form.pleaseComplete"),
                    minLength: {value: 3, message: "Bitte eine 3-stellige Nummer eingeben"},
                    maxLength: {value: 3, message: "Bitte eine 3-stellige Nummer eingeben"},
                  })}
                />
                {errors.number?.type === "required" && <span className={styles.error}>{errors.number.message}</span>}
                {errors.number && ["maxLength", "minLength"].includes(errors.number.type) && (
                  <span className={styles.error}>{errors.number.message}</span>
                )}
              </div>
            </Fragment>
          )}
        </div>

        <div className={styles.formRow}>
          <div
            className={`${styles.inputWrapper} ${styles.small}`}
            onChange={() => {
              setIsZipValid(true);
            }}
          >
            <input
              placeholder={t("form.zip") + " **"}
              {...register("zip", {
                required: true,
                maxLength: zipMaxLength(),
              })}
            />

            {(!isZipValid || ["maxLength"].includes(errors.zip?.type ?? "")) && watch().zip && (
              <span className={styles.error}>
                {t("form.typeInValidZip").replace("zip", isoLocales[getShopId()] === "en_GB" ? "post" : "zip")}
              </span>
            )}

            {errors.zip?.type === "required" && <span className={styles.error}>{t("form.pleaseComplete")}</span>}
          </div>

          <div className={`${styles.inputWrapper} ${styles.big}`}>
            <input
              placeholder={t("form.city") + " **"}
              {...register("city", {
                required: t("form.pleaseComplete"),
                maxLength: {
                  value: MAX_LENGTH,
                  message: t("form.maxOfCharacters", {count: MAX_LENGTH}),
                },
              })}
            />
            {errors.city && <span className={styles.error}>{errors.city.message}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div
            className={styles.inputWrapper}
            onChange={() => {
              setAddressPhoneError("");
            }}
          >
            <input
              type={"tel"}
              placeholder={`${t("form.phoneNumber")}`}
              {...register("phone", {
                maxLength: {
                  value: MAX_LENGTH,
                  message: t("form.maxOfCharacters", {count: MAX_LENGTH}),
                },
              })}
            />
            {!!addressPhoneError && <span className={styles.error}>{t(addressPhoneError)}</span>}
            {!!errors.phone && <span className={styles.error}>{errors.phone.message}</span>}
          </div>
          {isCorporateCustomer && <MobileIcon />}
        </div>

        {!customer.isLoggedIn && !isDelivery && (
          <div
            className={styles.inputWrapper}
            onChange={() => {
              setIsEmailValid(true);
            }}
          >
            <input
              type={"email"}
              placeholder={t("form.yourEmailAddress") + " **"}
              {...register("email", {
                required: true,
                pattern: emailRegExp,
                validate: mailCheckRun,
              })}
            />
            {errors.email?.type === "required" && <span className={styles.error}>{t("form.pleaseComplete")}</span>}
            {(["pattern", "validate"].includes(errors.email?.type ?? "none") || !isEmailValid) && watch().email && (
              <span className={styles.error}>{t("form.typeInValidEmailAddress")}</span>
            )}
          </div>
        )}
        {countries.length > 0 && (
          <div
            className={styles.inputWrapper}
            onChange={() => {
              setIsZipValid(true);
            }}
          >
            <select
              value={watch().countryCode}
              {...register("countryCode", {
                required: true,
              })}
              style={{color: !watch().countryCode ? "#a1a1a1" : "unset"}}
            >
              <option key="headline" value="" disabled={true}>
                {t("form.chooseCountry", {context: isDelivery ? "deliveryCountry" : undefined}) + " **"}
              </option>
              {countries.map((country) => {
                return (
                  <option key={country.countryCode} value={country.countryCode}>
                    {country.name}
                  </option>
                );
              })}
            </select>
            {errors.countryCode && <span className={styles.error}>{t("form.chooseCountry") + " **"}</span>}
          </div>
        )}

        {isCorporateCustomer && !isDelivery && (
          <div className={styles.inputWrapper}>
            <input placeholder={t("general.vatId")} {...register("vatNumber")} />
          </div>
        )}
        {states.length > 0 && (
          <div className={styles.inputWrapper}>
            <select
              value={watch().state || ""}
              {...register("state", {required: true})}
              style={{color: !watch().state ? "#a1a1a1" : "unset"}}
            >
              <option key="headline" value="" disabled={true}>
                {t("form.selectState") + " **"}
              </option>
              {states.map((state, index) => {
                return (
                  <option key={index} value={state.stateCode}>
                    {state.name}
                  </option>
                );
              })}
            </select>
            {errors.state && <span className={styles.error}>{t("form.selectState") + " **"}</span>}
          </div>
        )}
      </form>
    </div>
  );
};

export const DefaultCountryCode: {[shopID: number]: string} = {
  100001: "DE",
  100009: "AT",
  100017: "CH",
  100027: "CH",
  100005: "US",
  100020: "GB",
  100008: "FR",
  100022: "DK",
  100029: "NL",
};

export default AddressForm;
